import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { NavLink } from "react-router-dom";

import {
  EmailLink,
  Link,
  OverflowText,
  TextAvatar,
} from "@transfr-inc/dashboard-components";
import {
  BasicCell,
  Column,
  ColumnOptions,
  ColumnType,
  TableCellListContent,
  getSortKeyFunctionForTableCellList,
} from "@transfr-inc/dashboard-components/data-table";

import { ArchivedBadge } from "@components/custom/content";
import { createUserDetailLink } from "@lib/utils/navigation.utils";

import "./data-table-column-definition.scss";

export const getUserDetailPathConfig = ({
  breadcrumbLabel,
  classroomId,
  product,
}) => {
  return (value, row) => {
    return (
      <Link
        routerComponent={NavLink}
        to={createUserDetailLink({
          userId: row.userId,
          search: { classroomId, product, from: breadcrumbLabel },
        })}
      >
        {value}
      </Link>
    );
  };
};

export const DataTableColumnDefinition = {
  ORGANIZATION: new Column(
    "organizationNames",
    "Organizations",
    ColumnType.CUSTOM,
    new ColumnOptions({
      sortingEnabled: true,
      customFunction: (value, row) => {
        return (
          <TableCellListContent
            collection={value}
            collectionCount={row.organizationCount}
          />
        );
      },
      sortKeyFunctions: [
        getSortKeyFunctionForTableCellList(
          "organizationNames",
          "organizationCount"
        ),
      ],
    })
  ),
  FIRST_NAME: (linkFunction) =>
    new Column(
      "firstName",
      "First Name",
      ColumnType.CUSTOM,
      new ColumnOptions({
        sortingEnabled: true,
        overflowEnabled: true,
        customFunction: (value, row, rowIndex) => {
          const content = linkFunction ? (
            linkFunction(value, row, rowIndex)
          ) : (
            <span>{value}</span>
          );
          return (
            <div className="user-first-name-cell">
              <OverflowText text={value}>{content}</OverflowText>
              {row.isArchived && <ArchivedBadge />}
            </div>
          );
        },
      })
    ),
  LAST_NAME: (linkFunction) =>
    new Column(
      "lastName",
      "Last Name",
      linkFunction ? ColumnType.LINK : ColumnType.BASIC,
      new ColumnOptions({
        sortingEnabled: true,
        overflowEnabled: true,
        customFunction: linkFunction,
      })
    ),
  AVATAR: new Column(
    "avatar",
    "",
    ColumnType.CUSTOM,
    new ColumnOptions({
      customFunction: (value, row) =>
        row.isAnonymous ? (
          <div className="anonymous-container-avatar">
            <FontAwesomeIcon icon={["fa-solid", "user-secret"]} />
          </div>
        ) : (
          <TextAvatar text={[row.lastName, row.firstName]} />
        ),
    })
  ),
  EMAIL: new Column(
    "email",
    "Email",
    ColumnType.CUSTOM,
    new ColumnOptions({
      sortingEnabled: true,
      customFunction: (value) => {
        return value ? (
          <OverflowText text={value}>
            <EmailLink to={value} />
          </OverflowText>
        ) : (
          <BasicCell />
        );
      },
    })
  ),
  CLASSROOM: new Column(
    "classroomNames",
    "Classrooms",
    ColumnType.CUSTOM,
    new ColumnOptions({
      sortingEnabled: true,
      customFunction: (value, row) => {
        return (
          <TableCellListContent
            collection={value}
            collectionCount={row.classroomCount}
          />
        );
      },
      sortKeyFunctions: [
        getSortKeyFunctionForTableCellList("classroomNames", "classroomCount"),
      ],
    })
  ),
  USERNAME: new Column(
    "username",
    "Username",
    ColumnType.CUSTOM,
    new ColumnOptions({
      sortingEnabled: true,
      customFunction: (value) =>
        value ? (
          <OverflowText text={value}>
            <span>{value}</span>
          </OverflowText>
        ) : (
          <BasicCell />
        ),
    })
  ),
  LAST_LOGIN: new Column(
    "latestDashboardLogin",
    "Last Login",
    ColumnType.CUSTOM,
    new ColumnOptions({
      sortingEnabled: true,
      customFunction: (value) => (
        <BasicCell
          value={
            value &&
            new Date(value).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })
          }
        />
      ),
      sortKeyFunctions: [
        (row) =>
          row.latestDashboardLogin
            ? new Date(row.latestDashboardLogin).getTime()
            : 0,
      ],
    })
  ),
};
